import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { driver } from 'driver.js';
import { ApiService } from './api.service';
import { DataService } from './data.service';
import { Utility } from 'src/app/classes/common/utility';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import {DemoService} from './demo.service';

type StepMeta = {
  id: string;
  selector: string;
  text: string;
  route: string;
  onNextNavigate?: string;
  advanceOnRoute?: string;
  nextBtnText?: string;
  autoClickSelector?: string;
  waitForSelectorAfterClick?: string;
};

@Injectable({
  providedIn: 'root'
})
export class StepGuideService {

  private driverInstance: any;
  private guideActive = false;
  private currentStepIndex = 0;
  private driverStarted = false;
  private navigationSub: Subscription | null;
  private keydownHandler: ((event: KeyboardEvent) => void) | null;
  private clickHandler: ((event: MouseEvent) => void) | null;
  private pendingAdvanceRoute: string | null;
  private resolvedSteps: StepMeta[] | null = null;
  private stepGuideTranslations: any = null;
  private sessionCompleted = false;
  private sessionLoginTrigger = false;

  private buildSteps(): StepMeta[] {
    return [
      {
        id: 'step-map-marker',
        selector: '[data-testid="step-guide-map-marker"]',
        text: this.t('steps.map_marker'),
        route: 'map',
        nextBtnText: this.t('actions.next'),
        autoClickSelector: '[data-testid="step-guide-map-marker"]',
        waitForSelectorAfterClick: '[data-testid="step-guide-vehicle-detail"]'
      },
      {
        id: 'step-vehicle-detail',
        selector: '[data-testid="step-guide-vehicle-detail"]',
        text: this.t('steps.vehicle_detail'),
        route: 'map'
      },
      {
        id: 'step-card-address',
        selector: '[data-testid="step-guide-card-address"]',
        text: this.t('steps.card_address'),
        route: 'map'
      },
      {
        id: 'step-card-travel-tech',
        selector: '[data-testid="step-guide-card-travel-tech"]',
        text: this.t('steps.card_travel_tech'),
        route: 'map'
      },
      {
        id: 'step-card-diagnostic',
        selector: '[data-testid="step-guide-card-diagnostic"]',
        text: this.t('steps.card_diagnostic'),
        route: 'map'
      },
      {
        id: 'step-card-history',
        selector: '[data-testid="step-guide-history-cta"]',
        text: this.t('steps.card_history'),
        route: 'map',
        advanceOnRoute: 'routes-history',
        nextBtnText: this.t('actions.go_history'),
        autoClickSelector: '[data-testid="step-guide-history-cta"]'
      },
      {
        id: 'step-history-map',
        selector: '[data-testid="step-guide-history-map"]',
        text: this.t('steps.history_map'),
        route: 'routes-history'
      },
      {
        id: 'step-topbar-alarms',
        selector: '[data-testid="step-guide-nav-alarms"]',
        text: this.t('steps.topbar_alarms'),
        route: 'routes-history',
        onNextNavigate: 'alarms',
        advanceOnRoute: 'alarms',
        nextBtnText: this.t('actions.go_alarms')
      },
      {
        id: 'step-alarms-main',
        selector: '[data-testid="step-guide-alarms-main"]',
        text: this.t('steps.alarms_main'),
        route: 'alarms'
      },
      {
        id: 'step-alarm-speed-trigger',
        selector: '[data-testid="step-guide-alarm-speed-trigger"]',
        text: this.t('steps.alarm_speed_trigger'),
        route: 'alarms',
        nextBtnText: this.t('actions.open'),
        autoClickSelector: '[data-testid="step-guide-alarm-speed-trigger"]',
        waitForSelectorAfterClick: '[data-testid="step-guide-alarm-speed-modal"]'
      },
      {
        id: 'step-alarm-speed-modal',
        selector: '[data-testid="step-guide-alarm-speed-modal"]',
        text: this.t('steps.alarm_speed_modal'),
        route: 'alarms'
      },
      {
        id: 'step-alarm-speed-next-1',
        selector: '[data-testid="step-guide-alarm-speed-next"]',
        text: this.t('steps.alarm_speed_next'),
        route: 'alarms',
        nextBtnText: this.t('actions.proceed'),
        autoClickSelector: '[data-testid="step-guide-alarm-speed-next"]',
        waitForSelectorAfterClick: '[data-testid="step-guide-alarm-speed-input"]'
      },
      {
        id: 'step-alarm-speed-input',
        selector: '[data-testid="step-guide-alarm-speed-input"]',
        text: this.t('steps.alarm_speed_input'),
        route: 'alarms'
      },
      {
        id: 'step-alarm-speed-back',
        selector: '[data-testid="step-guide-alarm-speed-cancel"]',
        text: this.t('steps.alarm_speed_back'),
        route: 'alarms',
        nextBtnText: this.t('actions.back'),
        autoClickSelector: '[data-testid="step-guide-alarm-speed-cancel"]',
        waitForSelectorAfterClick: '[data-testid="step-guide-alarm-speed-modal"]'
      },
      {
        id: 'step-alarm-speed-close',
        selector: '[data-testid="step-guide-alarm-speed-cancel"]',
        text: this.t('steps.alarm_speed_close'),
        route: 'alarms',
        nextBtnText: this.t('actions.close'),
        autoClickSelector: '[data-testid="step-guide-alarm-speed-cancel"]',
        waitForSelectorAfterClick: '[data-testid="step-guide-alarms-main"]'
      },
      {
        id: 'step-go-activation',
        selector: '[data-testid="step-guide-nav-activation"]',
        text: this.t('steps.go_activation'),
        route: 'alarms',
        onNextNavigate: 'activation',
        advanceOnRoute: 'activation',
        nextBtnText: this.t('actions.go_activation')
      },
      {
        id: 'step-activation',
        selector: '[data-testid="step-guide-activation"]',
        text: this.t('steps.activation'),
        route: 'activation',
        nextBtnText: this.t('actions.continue')
      },
      {
        id: 'step-topbar-report',
        selector: '[data-testid="step-guide-nav-report"]',
        text: this.t('steps.topbar_report'),
        route: 'activation',
        onNextNavigate: 'report',
        advanceOnRoute: 'report',
        nextBtnText: this.t('actions.go_reports')
      },
      {
        id: 'step-report-main',
        selector: '[data-testid="step-guide-report-main"]',
        text: this.t('steps.report_main'),
        route: 'report'
      },
      {
        id: 'step-report-select',
        selector: '[data-testid="step-guide-report-select"]',
        text: this.t('steps.report_select'),
        route: 'report',
        nextBtnText: this.t('actions.select_vehicle'),
        autoClickSelector: '[data-testid="step-guide-report-select-action"]'
      },
      {
        id: 'step-report-request',
        selector: '[data-testid="step-guide-report-request"]',
        text: this.t('steps.report_request'),
        route: 'report',
        nextBtnText: this.t('actions.generate_report'),
        autoClickSelector: '[data-testid="step-guide-report-request"]',
        waitForSelectorAfterClick: '[data-testid="step-guide-report-detail"]'
      },
      {
        id: 'step-report-detail',
        selector: '[data-testid="step-guide-report-detail"]',
        text: this.t('steps.report_detail'),
        route: 'report'
      },
      {
        id: 'step-report-detail-close',
        selector: '[data-testid="step-guide-report-detail-close"]',
        text: this.t('steps.report_detail_close'),
        route: 'report',
        nextBtnText: this.t('actions.close'),
        autoClickSelector: '[data-testid="step-guide-report-detail-close"]',
        waitForSelectorAfterClick: '[data-testid="step-guide-report-main"]'
      },
      {
        id: 'step-open-sidebar-users',
        selector: '[data-testid="topbar-menu-button"]',
        text: this.t('steps.open_menu_users'),
        route: 'report',
        nextBtnText: this.t('actions.open_menu'),
        autoClickSelector: '[data-testid="topbar-menu-button"]',
        waitForSelectorAfterClick: '[data-testid="step-guide-sidebar-users"]'
      },
      {
        id: 'step-sidebar-users',
        selector: '[data-testid="step-guide-sidebar-users"]',
        text: this.t('steps.sidebar_users'),
        route: 'report',
        advanceOnRoute: 'users',
        nextBtnText: this.t('actions.go_users'),
        autoClickSelector: '[data-testid="step-guide-sidebar-users"]'
      },
      {
        id: 'step-users-main',
        selector: '[data-testid="step-guide-users-main"]',
        text: this.t('steps.users_main'),
        route: 'users'
      },
      {
        id: 'step-open-sidebar-notifications',
        selector: '[data-testid="topbar-menu-button"]',
        text: this.t('steps.open_menu_notifications'),
        route: 'users',
        nextBtnText: this.t('actions.open_menu'),
        autoClickSelector: '[data-testid="topbar-menu-button"]',
        waitForSelectorAfterClick: '[data-testid="step-guide-sidebar-notifications"]'
      },
      {
        id: 'step-sidebar-notifications',
        selector: '[data-testid="step-guide-sidebar-notifications"]',
        text: this.t('steps.sidebar_notifications'),
        route: 'users',
        advanceOnRoute: 'notifications',
        nextBtnText: this.t('actions.go_notifications'),
        autoClickSelector: '[data-testid="step-guide-sidebar-notifications"]'
      },
      {
        id: 'step-notifications-main',
        selector: '[data-testid="step-guide-notifications-main"]',
        text: this.t('steps.notifications_main'),
        route: 'notifications',
        nextBtnText: this.t('actions.done')
      }
    ];
  }

  constructor(
    private router: Router,
    private api: ApiService,
    private data: DataService,
    private zone: NgZone,
    private translate: TranslateService
  ) {
    // Trigger welcome on first map entry after a page load
    this.sessionLoginTrigger = true;
    AuthService.hasLogged$.subscribe(() => {
      this.sessionLoginTrigger = true;
      this.sessionCompleted = false;
    });
  }

  private t(path: string): string{
    if(!this.stepGuideTranslations) return path;
    const value = path.split('.').reduce((acc: any, key: string) => {
      if(acc && acc[key] !== undefined) return acc[key];
      return undefined;
    }, this.stepGuideTranslations);
    return value !== undefined ? value : path;
  }

  private async loadTranslations(){
    try{
      this.stepGuideTranslations = await firstValueFrom(this.translate.get('STEP_GUIDE'));
    }catch(err){
      console.error(err);
      this.stepGuideTranslations = null;
    }
  }

  markRegistrationCompleted(){
    // no-op: guide is demo-only and not persisted
  }

  async syncPendingServerFlag(){
    // no-op: guide is demo-only and not persisted
  }

  async maybeStartGuide(){
    try{
      if(this.guideActive) return;
      if(!Utility.isDesktop()) return;
      if(!this.data?.selOrganization || !this.data.selOrganization._id || !this.data.selOrganization.me?._id) return;
      if(!this.isDemoOrg()) return;
      if(this.sessionCompleted) return;
      await this.startGuideFlow();
    }catch(err){
      console.error(err);
      this.terminateGuide();
    }
  }

  shouldShowWelcome(): boolean{
    if(this.guideActive) return false;
    if(!Utility.isDesktop()) return false;
    if(!this.data?.selOrganization || !this.data.selOrganization._id || !this.data.selOrganization.me?._id) return false;
    if(!this.isDemoOrg()) return false;
    if(this.sessionCompleted) return false;
    return true;
  }

  markWelcomeShown(){
    // no-op: guide is demo-only and not persisted
  }

  async startGuideFromWelcome(){
    try{
      if(this.guideActive) return;
      if(!this.isDemoOrg()) return;
      await this.startGuideFlow();
    }catch(err){
      console.error(err);
      this.terminateGuide();
    }
  }

  async skipGuideFromWelcome(){
    try{
      if(!this.isDemoOrg()) return;
      // no persistence for demo guide
    }catch(err){
      console.error(err);
    }
  }

  async startGuideOnDemand(){
    try{
      if(this.guideActive) return;
      if(!this.isDemoOrg()) return;
      this.sessionCompleted = false;
      await this.startGuideFlow();
    }catch(err){
      console.error(err);
    }
  }

  private async startGuideFlow(){
    if(!this.isDemoOrg()) return;
    await this.loadTranslations();
    this.resolvedSteps = await this.resolveSteps();
    this.initDriver();
    this.bindGlobalHandlers();
    this.subscribeToNavigation();
    this.guideActive = true;
    await this.showStep(0);
  }

  private async skipGuideFlow(){
    // no-op: guide is demo-only and not persisted
  }

  private async markGuideStarted(){
    // no-op: guide is demo-only and not persisted
  }

  private initDriver(){
    if(this.driverInstance) return;
    const stepsSource = this.resolvedSteps || this.buildSteps();
    const steps: any[] = stepsSource.map((step) => ({
      element: step.selector,
      popover: {
        description: step.text,
        nextBtnText: step.nextBtnText || this.t('actions.next'),
        prevBtnText: '',
        onNextClick: () => this.onNextClick(step),
        onPrevClick: () => this.onPrevClick()
      }
    }));

    this.driverInstance = driver({
      steps: steps,
      showProgress: true,
      allowClose: true,
      overlayOpacity: 0.2,
      onDestroyed: () => {
        if(this.guideActive){
          this.finishGuide(false);
        }
      },
      onDestroyStarted: () => {
        if(this.guideActive){
          this.finishGuide(false);
        }
      }
    });
  }

  private isDemoOrg(): boolean{
    // Nella demo pubblica questa guida non deve partire: chi entra da /demo ha
    // il giro breve di app-demo-tour, cinque passi invece di ventinove. Sono
    // due percorsi per due pubblici diversi — qui è dove si separano, e ci
    // passano tutti i punti d'ingresso della guida (maybeStartGuide,
    // startGuideOnDemand, shouldShowWelcome).
    if(DemoService.active) return false;
    return this.data?.selOrganization?.login_view_demo;
  }

  isLoginTriggered(): boolean{
    return this.sessionLoginTrigger;
  }

  clearLoginTrigger(): void{
    if(!this.sessionLoginTrigger) return;
    this.sessionLoginTrigger = false;
  }

  async waitForOrgReady(timeoutMs: number = 8000): Promise<boolean>{
    const start = Date.now();
    while(Date.now() - start < timeoutMs){
      if(this.data?.selOrganization?._id && this.data.selOrganization.me?._id){
        return true;
      }
      await Utility.wait(200);
    }
    return false;
  }

  private async showStep(index: number){
    const stepsSource = this.resolvedSteps || this.buildSteps();
    const step = stepsSource[index];
    if(!step){
      this.finishGuide(true);
      return;
    }

    const route = this.getRouteFromUrl(this.router.url);
    if(step.route && route !== step.route){
      if(step.onNextNavigate){
        this.router.navigateByUrl(step.onNextNavigate);
        this.pendingAdvanceRoute = step.onNextNavigate;
        return;
      }
      this.terminateGuide();
      return;
    }

    const element = await this.waitForElement(step.selector, 15000);
    if(!element){
      this.terminateGuide();
      return;
    }

    this.currentStepIndex = index;
    this.pendingAdvanceRoute = step.advanceOnRoute || null;

    if(this.driverInstance?.drive){
      if(!this.driverStarted){
        this.driverInstance.drive(index);
        this.driverStarted = true;
        return;
      }
      if(this.driverInstance?.moveTo){
        try{
          this.driverInstance.moveTo(index);
          return;
        }catch(err){}
      }
      this.driverInstance.drive(index);
    }
  }

  private onNextClick(step: StepMeta){
    if(step.autoClickSelector){
      const el = document.querySelector(step.autoClickSelector) as HTMLElement;
      if(!el){
        this.terminateGuide();
        return;
      }
      el.click();
      if(step.advanceOnRoute){
        this.pendingAdvanceRoute = step.advanceOnRoute;
        return;
      }
      if(step.waitForSelectorAfterClick){
        this.waitForElement(step.waitForSelectorAfterClick, 8000).then((nextEl) => {
          if(!nextEl){
            this.terminateGuide();
            return;
          }
          this.moveToStep(this.currentStepIndex + 1);
        });
        return;
      }
    }
    if(step.onNextNavigate){
      this.pendingAdvanceRoute = step.onNextNavigate;
      this.router.navigateByUrl(step.onNextNavigate);
      return;
    }
    this.moveToStep(this.currentStepIndex + 1);
  }

  private onPrevClick(){
    this.moveToStep(Math.max(this.currentStepIndex - 1, 0));
  }

  private moveToStep(index: number){
    this.zone.run(() => {
      this.showStep(index);
    });
  }

  private subscribeToNavigation(){
    if(this.navigationSub) return;
    this.navigationSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationStart || event instanceof NavigationEnd))
      .subscribe((event: any) => {
        if(!this.guideActive) return;
        if(event instanceof NavigationStart) return;

        const route = this.getRouteFromUrl(event.urlAfterRedirects || event.url);
        if(this.pendingAdvanceRoute && route === this.pendingAdvanceRoute){
          const nextIndex = this.currentStepIndex + 1;
          this.pendingAdvanceRoute = null;
          this.moveToStep(nextIndex);
          return;
        }

        const stepsSource = this.resolvedSteps || this.buildSteps();
        const expectedRoute = stepsSource[this.currentStepIndex]?.route;
        if(expectedRoute && route !== expectedRoute){
          this.terminateGuide();
        }
      });
  }

  private bindGlobalHandlers(){
    if(this.keydownHandler || this.clickHandler) return;

    this.keydownHandler = (event: KeyboardEvent) => {
      if(event.key === 'Escape'){
        this.terminateGuide();
      }
    };
    document.addEventListener('keydown', this.keydownHandler, true);

    this.clickHandler = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if(!target) return;
      const closeBtn = target.classList.contains('driver-popover-close-btn') || target.closest?.('.driver-popover-close-btn');
      const overlay = target.classList.contains('driver-overlay') || target.closest?.('.driver-overlay');
      if(closeBtn || overlay){
        this.terminateGuide();
      }
    };
    document.addEventListener('click', this.clickHandler, true);
  }

  private unbindGlobalHandlers(){
    if(this.keydownHandler){
      document.removeEventListener('keydown', this.keydownHandler, true);
      this.keydownHandler = null;
    }
    if(this.clickHandler){
      document.removeEventListener('click', this.clickHandler, true);
      this.clickHandler = null;
    }
  }

  private terminateGuide(){
    if(!this.guideActive) return;
      this.finishGuide(false);
  }

  private finishGuide(completed: boolean){
    try{
      this.guideActive = false;
      this.pendingAdvanceRoute = null;
      if(completed){
        this.sessionCompleted = true;
      }
      if(this.driverInstance?.destroy) this.driverInstance.destroy();
      this.driverInstance = null;
      this.driverStarted = false;
      this.resolvedSteps = null;
      if(this.navigationSub){
        this.navigationSub.unsubscribe();
        this.navigationSub = null;
      }
      this.unbindGlobalHandlers();
      if(completed){
        const route = this.getRouteFromUrl(this.router.url);
        if(route !== 'map'){
          this.router.navigateByUrl('map');
        }
      }
    }catch(err){
      console.error(err);
    }
  }

  private getRouteFromUrl(url: string): string{
    if(!url) return '';
    const clean = url.split('?')[0].split('#')[0];
    return clean.replace(/^\/+/, '').split('/')[0] || '';
  }

  private waitForElement(selector: string, timeoutMs: number){
    return new Promise<HTMLElement | null>((resolve) => {
      const start = Date.now();
      const tick = () => {
        const el = document.querySelector(selector) as HTMLElement;
        if(el){
          resolve(el);
          return;
        }
        if(Date.now() - start >= timeoutMs){
          resolve(null);
          return;
        }
        requestAnimationFrame(tick);
      };
      tick();
    });
  }

  private async resolveSteps(): Promise<StepMeta[]>{
    const stepsCopy = this.buildSteps().map((step) => ({...step}));
    const markerSelector = stepsCopy[0]?.selector;
    if(markerSelector){
      const marker = await this.waitForElement(markerSelector, 2000);
      if(!marker){
        stepsCopy[0].selector = '[data-testid="step-guide-main-map"]';
      }
    }
    return stepsCopy;
  }

}
